import React, { useEffect, useState } from "react";
import { Share, Plus, X, Smartphone } from "lucide-react";

const DISMISS_KEY = "tme_ios_install_dismissed_v1";

/** Detects iOS Safari (including iPadOS 13+ which masquerades as MacIntel)
 *  and whether the page is already running as an installed Home Screen app. */
function detectInstallable() {
  if (typeof window === "undefined") return { isIOS: false, isStandalone: true, isSafari: false };
  const ua = window.navigator.userAgent;
  const platform = window.navigator.platform || "";
  const maxTouch = window.navigator.maxTouchPoints || 0;
  const isIOS =
    /iPad|iPhone|iPod/.test(ua) ||
    (platform === "MacIntel" && maxTouch > 1); // iPadOS 13+
  // iOS Safari only — Chrome/Firefox/Edge on iOS use CriOS/FxiOS/EdgiOS in UA.
  const isSafari =
    /Safari/i.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/i.test(ua);
  const isStandalone =
    window.navigator.standalone === true ||
    (typeof window.matchMedia === "function" &&
      window.matchMedia("(display-mode: standalone)").matches);
  return { isIOS, isStandalone, isSafari };
}

/** Lightweight, dismissible bottom-sheet prompt instructing iOS Safari users
 *  to add the dashboard to their Home Screen. Renders nothing if already
 *  installed, dismissed, or running outside iOS Safari. */
export default function IosInstallPrompt() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const { isIOS, isStandalone, isSafari } = detectInstallable();
    if (!isIOS || isStandalone || !isSafari) return;
    try {
      if (window.localStorage.getItem(DISMISS_KEY) === "1") return;
    } catch (err) {
      // localStorage can throw when blocked (Safari private mode, etc).
      // Surface it for debugging but still show the prompt.
      // eslint-disable-next-line no-console
      console.debug("IosInstallPrompt: localStorage read blocked", err);
    }
    // Tiny delay so it doesn't fight the page entering.
    const t = setTimeout(() => setShow(true), 600);
    return () => clearTimeout(t);
  }, []);

  const dismiss = () => {
    try {
      window.localStorage.setItem(DISMISS_KEY, "1");
    } catch (err) {
      // Best-effort persist — if storage is blocked the user will see the
      // prompt again next visit. Log for debugging only.
      // eslint-disable-next-line no-console
      console.debug("IosInstallPrompt: localStorage write blocked", err);
    }
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      data-testid="ios-install-prompt"
      role="dialog"
      aria-label="Install Modern Exit Ops on Home Screen"
      className="fixed inset-x-0 bottom-0 z-[60] px-4 pb-[max(env(safe-area-inset-bottom),16px)] pt-3 pointer-events-none"
    >
      <div className="pointer-events-auto mx-auto max-w-md rounded-3xl border border-sand-300/80 bg-ink-deep text-sand-100 shadow-2xl backdrop-blur">
        <div className="relative px-5 pt-5 pb-4">
          <button
            type="button"
            onClick={dismiss}
            data-testid="ios-install-dismiss"
            aria-label="Dismiss install prompt"
            className="absolute top-3 right-3 inline-flex h-8 w-8 items-center justify-center rounded-full text-sand-100/60 hover:text-sand-100 hover:bg-sand-100/10 transition-colors"
          >
            <X size={15} />
          </button>

          <div className="flex items-start gap-3">
            <span className="inline-flex h-10 w-10 grid place-items-center rounded-2xl bg-clay/20 text-clay border border-clay/40 shrink-0">
              <Smartphone size={16} />
            </span>
            <div className="min-w-0 pr-6">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay">
                Install · iOS
              </p>
              <h2 className="font-display mt-1.5 text-[17px] leading-snug font-semibold text-sand-100">
                Add ME Ops to your Home Screen
              </h2>
              <p className="mt-1.5 text-[12.5px] text-sand-100/70 leading-relaxed">
                Open intakes like a native app — no Safari chrome, fullscreen, one tap from your dock.
              </p>
            </div>
          </div>

          <ol className="mt-4 space-y-2">
            <li className="flex items-center gap-3 rounded-xl border border-sand-100/10 bg-sand-100/[0.04] px-3 py-2.5">
              <span className="font-mono text-[10px] text-clay tabular-nums w-4">1</span>
              <span className="inline-flex h-7 w-7 grid place-items-center rounded-md bg-sand-100/10 text-sand-100 border border-sand-100/15">
                <Share size={13} />
              </span>
              <span className="text-[12.5px] text-sand-100/90 leading-snug">
                Tap <span className="font-medium">Share</span> in Safari
              </span>
            </li>
            <li className="flex items-center gap-3 rounded-xl border border-sand-100/10 bg-sand-100/[0.04] px-3 py-2.5">
              <span className="font-mono text-[10px] text-clay tabular-nums w-4">2</span>
              <span className="inline-flex h-7 w-7 grid place-items-center rounded-md bg-sand-100/10 text-sand-100 border border-sand-100/15">
                <Plus size={13} />
              </span>
              <span className="text-[12.5px] text-sand-100/90 leading-snug">
                Choose <span className="font-medium">Add to Home Screen</span>
              </span>
            </li>
          </ol>

          <button
            type="button"
            onClick={dismiss}
            data-testid="ios-install-got-it"
            className="mt-4 w-full inline-flex items-center justify-center rounded-full bg-sand-100 text-ink-deep px-5 py-3 text-[13px] font-medium hover:bg-clay hover:text-sand-100 transition-colors min-h-[44px]"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}
